import type { TaskAvailability, ScheduleTask } from '../types/ScheduleTask';

// Half-open [beginning, end): available when now ∈ [beginning, end), upcoming
// before, expired at/after end. Matches the PROSE/Services window convention.
export const classifyAvailability = (
  now: Date,
  beginning: Date,
  end: Date
): TaskAvailability => {
  if (now < beginning) return 'upcoming';
  if (now < end) return 'available';
  return 'expired';
};

// The single rule for whether a task appears in any schedule listing
// (home or calendar): not completed and not past its availability window.
// Tasks outside this rule are reachable only by direct URL; the task page
// gates on availability and task status, showing the not-available page for
// completed, upcoming, or expired tasks instead of an editable form.
export const isListableTask = (task: ScheduleTask): boolean =>
  task.progress !== 'done' && task.availability !== 'expired';

// A missed task's window closed before it was completed. It is neither
// listable nor presentable, and the enrollment landing treats a missed
// phase task as a closed enrollment.
export const isMissedTask = (task: ScheduleTask): boolean =>
  task.progress !== 'done' && task.availability === 'expired';

// An auto-present task is the target of the enrollment landing's next-step CTA.
// Requires: PROSE DSL `auto_present` flag, the task window is currently open
// (available — not upcoming or expired), and the task is not yet completed. The
// CTA always points at an unfinished questionnaire — whether unstarted (`new`)
// or resumed (`in-progress`); a completed (`done`) task is never targeted.
export const isAutoPresentTask = (task: ScheduleTask): boolean =>
  task.autoPresent &&
  task.availability === 'available' &&
  task.progress !== 'done';
